import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Truck, Package } from 'lucide-react';
import { api } from '../../../api/client';
import { EntityLink } from '../../../components/shared/EntityLink';
import { hiRes } from '../shared';

export function ReadyToShipTab({ sellerId, sellers }: { sellerId: number; sellers: any[] }) {
  const { t } = useTranslation();
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const ids = sellers.map(s => s.id).join(',');
        const res: any = await api.get(`/warehouse/ready-to-ship?seller_id=${sellerId}&seller_ids=${ids}&limit=100`);
        if (res.success) { setOrders(res.data || []); setTotal(res.meta?.total || 0); }
      } catch (err) { console.error('Failed to load RTS', err); }
      finally { setLoading(false); }
    })();
  }, [sellerId, sellers]);

  if (loading) return <div className="py-8 text-center text-[var(--color-text-secondary)]">{t('common.loading')}</div>;

  const daysAgo = (dateStr: string) => {
    return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  };

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-medium text-[var(--color-text)]">{total} {t('wh.ordersReady')}</h3>
      {orders.length === 0 ? (
        <div className="text-center py-8 text-[var(--color-text-secondary)]">
          <Truck size={40} className="mx-auto mb-3 opacity-50" /><p>{t('wh.noRTS')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {orders.map((o: any, i: number) => {
            const days = daysAgo(o.date_created);
            return (
              <div key={i} className="flex items-center gap-3 p-3 bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-xl hover:bg-[var(--color-bg-hover)]">
                {/* 48px thumbnail */}
                {o.thumbnail ? (
                  <img src={hiRes(o.thumbnail) || o.thumbnail} alt="" className="w-12 h-12 rounded-lg object-cover bg-white shrink-0 shadow" />
                ) : (
                  <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-lg shrink-0 flex items-center justify-center">
                    <Package size={20} className="text-gray-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  {/* Title (never truncated) */}
                  <p className="text-sm text-[var(--color-text)] font-medium leading-snug">{o.title}</p>
                  <div className="flex items-center gap-3 text-xs text-[var(--color-text-secondary)] mt-1 flex-wrap">
                    <EntityLink type="ml_order" value={o.ml_order_id} sellerId={sellerId} />
                    <span>{o.buyer_nickname}</span>
                    {o.seller_sku && <EntityLink type="asin" value={o.seller_sku.substring(0, 10)} sellerId={sellerId} />}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {/* Seller badge */}
                  {o.seller_nickname && (
                    <span className="px-2 py-0.5 rounded-lg text-[10px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                      {o.seller_nickname}
                    </span>
                  )}
                  {/* Processed-in-warehouse indicator (yellow) takes precedence over stock (green) */}
                  {o.wh_processed ? (
                    <span className="px-2 py-0.5 rounded-lg text-[10px] font-semibold bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300 whitespace-nowrap">
                      {t('wh.lot')} #{o.wh_batch_num} · {o.wh_bag_code ? `${t('wh.bag')} ${o.wh_bag_code}` : t('wh.noBag')}
                    </span>
                  ) : o.has_stock ? (
                    <span className="text-green-600 text-xs font-semibold">{t('wh.stock')}</span>
                  ) : (
                    <span className="text-red-500 text-xs">--</span>
                  )}
                  {/* Days badge */}
                  {days > 3 && (
                    <span className="px-2 py-0.5 rounded-lg text-xs font-semibold bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300">
                      {days}d
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
